import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, Sun, Moon, ExternalLink, Copy, CheckCircle, ArrowLeft } from 'lucide-react';
import { themeClasses } from '../theme/classes';

const LoginPage = ({ language = 'th', onChangeLanguage, themeMode = 'light', onToggleTheme, onBack }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const { login, signup, loginWithGoogle, resetPassword } = useAuth();
  const [linkCopied, setLinkCopied] = useState(false);

  // Detect in-app browsers (LINE, Facebook, Instagram, etc.)
  const isInAppBrowser = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /Line|FBAN|FBAV|Instagram|Twitter|MicroMessenger|KAKAOTALK|Snapchat|TikTok|Threads/i.test(ua);
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    }).catch(() => {});
  };

  const handleOpenExternal = () => {
    // iOS: x-safari-https scheme, Android: intent scheme
    const url = window.location.href;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      window.location.href = url.replace(/^https?:\/\//, 'x-safari-https://');
    } else {
      window.open(url, '_system');
    }
  };

  const labels = language === 'en'
    ? {
      tagline: 'Manage your investment portfolio',
      login: 'Log in',
      signup: 'Sign up',
      googleLogin: 'Continue with Google',
      loggingIn: 'Signing in...',
      processing: 'Processing...',
      or: 'or',
      displayName: 'Display name',
      displayNamePlaceholder: 'Your public name',
      email: 'Email',
      password: 'Password',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
      forgotPassword: 'Forgot password?',
      forgotPasswordTitle: 'Reset Password',
      forgotPasswordDesc: 'Enter the email address associated with your account and we will send you a link to reset your password.',
      forgotPasswordEmail: 'Email address',
      forgotPasswordSend: 'Send Reset Link',
      forgotPasswordSending: 'Sending...',
      forgotPasswordSuccess: 'Password reset email sent! Please check your inbox (and spam folder).',
      forgotPasswordBack: 'Back to login',
      footer: '© 2025 PortPro. All rights reserved.',
      language: 'Language',
      thai: 'ไทย',
      english: 'English',
      errors: {
        passwordShort: 'Password must be at least 6 characters',
        userNotFound: 'No account found for this email',
        wrongPassword: 'Incorrect password',
        invalidCredential: 'Incorrect email or password. Please check and try again.',
        emailInUse: 'This email is already in use',
        invalidEmail: 'Invalid email format',
        weakPassword: 'Password must be at least 6 characters',
        tooManyRequests: 'Too many failed attempts. Please try again later.',
        generic: 'Something went wrong. Please try again.',
        googleCancelled: 'Sign-in was cancelled',
        googleFailed: 'Unable to sign in with Google'
      }
    }
    : {
      tagline: 'จัดการพอร์ตการลงทุนของคุณ',
      login: 'เข้าสู่ระบบ',
      signup: 'สมัครสมาชิก',
      googleLogin: 'เข้าสู่ระบบด้วย Google',
      loggingIn: 'กำลังเข้าสู่ระบบ...',
      processing: 'กำลังดำเนินการ...',
      or: 'หรือ',
      displayName: 'ชื่อผู้ใช้',
      displayNamePlaceholder: 'ชื่อที่ต้องการแสดง',
      email: 'อีเมล',
      password: 'รหัสผ่าน',
      noAccount: 'ยังไม่มีบัญชี?',
      haveAccount: 'มีบัญชีอยู่แล้ว?',
      forgotPassword: 'ลืมรหัสผ่าน?',
      forgotPasswordTitle: 'รีเซ็ตรหัสผ่าน',
      forgotPasswordDesc: 'กรอกอีเมลที่ใช้สมัครสมาชิก ระบบจะส่งลิงก์สำหรับตั้งรหัสผ่านใหม่ไปให้',
      forgotPasswordEmail: 'อีเมล',
      forgotPasswordSend: 'ส่งลิงก์รีเซ็ต',
      forgotPasswordSending: 'กำลังส่ง...',
      forgotPasswordSuccess: 'ส่งอีเมลรีเซ็ตรหัสผ่านแล้ว! กรุณาตรวจสอบกล่องจดหมาย (รวมถึงโฟลเดอร์สแปม)',
      forgotPasswordBack: 'กลับไปหน้าล็อกอิน',
      footer: '© 2025 PortPro สงวนลิขสิทธิ์',
      language: 'ภาษา',
      thai: 'ไทย',
      english: 'English',
      errors: {
        passwordShort: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร',
        userNotFound: 'ไม่พบบัญชีผู้ใช้นี้',
        wrongPassword: 'รหัสผ่านไม่ถูกต้อง',
        invalidCredential: 'อีเมลหรือรหัสผ่านไม่ถูกต้อง กรุณาตรวจสอบแล้วลองใหม่',
        emailInUse: 'อีเมลนี้ถูกใช้งานแล้ว',
        invalidEmail: 'รูปแบบอีเมลไม่ถูกต้อง',
        weakPassword: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร',
        tooManyRequests: 'ล็อกอินผิดหลายครั้งเกินไป กรุณารอสักครู่แล้วลองใหม่',
        generic: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
        googleCancelled: 'การเข้าสู่ระบบถูกยกเลิก',
        googleFailed: 'ไม่สามารถเข้าสู่ระบบด้วย Google ได้'
      }
    };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        if (password.length < 6) {
          throw new Error(labels.errors.passwordShort);
        }
        await signup(email, password, displayName);
      }
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/user-not-found') {
        setError(labels.errors.userNotFound);
      } else if (err.code === 'auth/wrong-password') {
        setError(labels.errors.wrongPassword);
      } else if (err.code === 'auth/invalid-credential') {
        setError(labels.errors.invalidCredential);
      } else if (err.code === 'auth/email-already-in-use') {
        setError(labels.errors.emailInUse);
      } else if (err.code === 'auth/invalid-email') {
        setError(labels.errors.invalidEmail);
      } else if (err.code === 'auth/weak-password') {
        setError(labels.errors.weakPassword);
      } else if (err.code === 'auth/too-many-requests') {
        setError(labels.errors.tooManyRequests);
      } else {
        setError(err.message || labels.errors.generic);
      }
    }

    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError(labels.errors.googleCancelled);
      } else {
        setError(labels.errors.googleFailed);
      }
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');
    setResetLoading(true);

    try {
      await resetPassword(resetEmail);
      setResetMessage(labels.forgotPasswordSuccess);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setResetError(labels.errors.userNotFound);
      } else if (err.code === 'auth/invalid-email') {
        setResetError(labels.errors.invalidEmail);
      } else if (err.code === 'auth/too-many-requests') {
        setResetError(labels.errors.tooManyRequests);
      } else {
        setResetError(labels.errors.generic);
      }
    }

    setResetLoading(false);
  };

  const handlePageRefresh = () => {
    if (typeof window !== 'undefined') {
      window.location.assign(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    }
  };

  const labelClassName = 'block text-sm font-medium theme-text-secondary mb-2';
  const iconClassName = 'absolute left-4 top-1/2 transform -translate-y-1/2 theme-text-muted';

  return (
    <div className={`${themeClasses.authPage} py-6 sm:p-4`}>
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={themeClasses.authGlowPrimary}></div>
        <div className={themeClasses.authGlowSecondary}></div>
        <div className={themeClasses.authGlowTertiary}></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Top bar: Back + Language + Theme */}
        <div className="flex items-center justify-between mb-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors theme-text-muted hover:opacity-80"
            >
              <ArrowLeft size={16} />
              {language === 'en' ? 'Back' : 'กลับ'}
            </button>
          ) : <div/>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onChangeLanguage?.('th')}
              className={language === 'th' ? themeClasses.authLanguageButtonActive : themeClasses.authLanguageButton}
            >
              {labels.thai}
            </button>
            <button
              type="button"
              onClick={() => onChangeLanguage?.('en')}
              className={language === 'en' ? themeClasses.authLanguageButtonActive : themeClasses.authLanguageButton}
            >
              {labels.english}
            </button>
            <button
              type="button"
              onClick={onToggleTheme}
              className={themeClasses.authThemeToggle}
              aria-label="Toggle theme"
            >
              {themeMode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>

        {/* Logo & Title */}
        <div className="text-center mb-5">
          <button
            type="button"
            onClick={handlePageRefresh}
            className="inline-flex flex-col items-center transition-opacity hover:opacity-90"
          >
            <img 
              src={`${process.env.PUBLIC_URL}/logo.png`} 
              alt="PortPro Logo" 
              className="w-14 h-14 rounded-2xl mb-3 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/10" 
            />
            <h1 className="text-2xl font-bold theme-text-primary">
              Port<span className="theme-accent-text">Pro</span><span className="text-base font-semibold opacity-50 ml-1">.app</span>
            </h1>
            <p className="theme-text-muted mt-1 text-sm">{labels.tagline}</p>
          </button>
        </div>

        {/* Login Card */}
        <div className={`${themeClasses.authCard} !p-6 sm:!p-8`}>
          <h2 className="text-xl font-bold theme-text-primary mb-5 text-center">
            {isLogin ? labels.login : labels.signup}
          </h2>

          {/* Error Message */}
          {error && (
            <div className={themeClasses.authError}>
              <AlertCircle size={20} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* In-App Browser Warning */}
          {isInAppBrowser && (
            <div className="mb-4 p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50">
              <div className="flex items-start gap-2 mb-2">
                <AlertCircle size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-bold text-amber-800 dark:text-amber-300">
                  {language === 'en' ? 'In-App Browser Detected' : 'ตรวจพบเบราว์เซอร์ในแอป'}
                </p>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-400/80 mb-3 leading-relaxed">
                {language === 'en'
                  ? 'Google Sign-In doesn\'t work in this browser. Please copy the link below and open it in Chrome or Safari.'
                  : 'การเข้าสู่ระบบ Google ไม่รองรับเบราว์เซอร์ในแอป (LINE, Facebook ฯลฯ) กรุณาคัดลอกลิงก์แล้วเปิดใน Chrome หรือ Safari'
                }
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-amber-600 text-white hover:bg-amber-700 active:scale-95 transition-all"
                >
                  {linkCopied ? <><CheckCircle size={14} /> {language === 'en' ? 'Copied!' : 'คัดลอกแล้ว!'}</> : <><Copy size={14} /> {language === 'en' ? 'Copy Link' : 'คัดลอกลิงก์'}</>}
                </button>
                <button
                  type="button"
                  onClick={handleOpenExternal}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-all"
                >
                  <ExternalLink size={14} />
                  {language === 'en' ? 'Open Browser' : 'เปิดเบราว์เซอร์'}
                </button>
              </div>
            </div>
          )}

          {/* Google Login Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading || isInAppBrowser}
            className={`${themeClasses.authGoogleButton} ${isInAppBrowser ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {loading ? labels.loggingIn : labels.googleLogin}
          </button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full ${themeClasses.authDivider}`}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-transparent theme-text-muted">{labels.or}</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className={labelClassName}>{labels.displayName}</label>
                <div className="relative">
                  <User className={iconClassName} size={20} />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={labels.displayNamePlaceholder}
                    className={themeClasses.authInput}
                  />
                </div>
              </div>
            )}

            <div>
              <label className={labelClassName}>{labels.email}</label>
              <div className="relative">
                <Mail className={iconClassName} size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className={themeClasses.authInput}
                />
              </div>
            </div>

            <div>
              <label className={labelClassName}>{labels.password}</label>
              <div className="relative">
                <Lock className={iconClassName} size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className={themeClasses.authInputWithAction}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors theme-text-muted hover:opacity-80"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {isLogin && (
              <div className="flex justify-end -mt-1">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setResetEmail(email);
                    setResetMessage('');
                    setResetError('');
                  }}
                  className={`text-xs ${themeClasses.authSecondaryButton}`}
                >
                  {labels.forgotPassword}
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={themeClasses.authPrimaryButton}
            >
              {loading ? labels.processing : (isLogin ? labels.login : labels.signup)}
            </button>
          </form>

          {/* Toggle Login/Signup */}
          <p className="mt-6 text-center theme-text-muted">
            {isLogin ? labels.noAccount : labels.haveAccount}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setShowForgotPassword(false);
              }}
              className={`ml-2 ${themeClasses.authSecondaryButton}`}
            >
              {isLogin ? labels.signup : labels.login}
            </button>
          </p>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
              <div className={`w-full max-w-sm rounded-2xl p-6 shadow-2xl ${themeClasses.authCard}`}>
                <h3 className="text-xl font-bold theme-text-primary mb-2">{labels.forgotPasswordTitle}</h3>
                <p className="text-sm theme-text-muted mb-5">{labels.forgotPasswordDesc}</p>

                {resetMessage && (
                  <div className="rounded-xl border px-4 py-3 mb-4 text-sm font-medium border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                    {resetMessage}
                  </div>
                )}

                {resetError && (
                  <div className={themeClasses.authError}>
                    <AlertCircle size={18} />
                    <span className="text-sm">{resetError}</span>
                  </div>
                )}

                {!resetMessage && (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className={labelClassName}>{labels.forgotPasswordEmail}</label>
                      <div className="relative">
                        <Mail className={iconClassName} size={20} />
                        <input
                          type="email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          placeholder="your@email.com"
                          required
                          className={themeClasses.authInput}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={resetLoading}
                      className={themeClasses.authPrimaryButton}
                    >
                      {resetLoading ? labels.forgotPasswordSending : labels.forgotPasswordSend}
                    </button>
                  </form>
                )}

                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className={`mt-4 w-full text-center text-sm ${themeClasses.authSecondaryButton}`}
                >
                  {labels.forgotPasswordBack}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-4 space-y-1.5">
          <p className={themeClasses.authFooter}>
            {labels.footer}
          </p>
          <a href="https://lin.ee/9Mgwhzg" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs theme-text-muted hover:opacity-80 transition-opacity">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-[#06C755]" fill="currentColor"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg>
            {language === 'en' ? 'Need help? Contact us' : 'ต้องการความช่วยเหลือ? ติดต่อเรา'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
